export const KINGS_CROWN_BOARD_SIZE = 5;
export const KINGS_CROWN_CENTER = 2;
export const KINGS_CROWN_INITIAL_HAND_SIZE = 2;
export const KINGS_CROWN_MAX_HAND_SIZE = 12;
export const KINGS_CROWN_TABLE_SLOT_COUNT = 40;
export const KINGS_CROWN_CHIP_NUMBERS = Array.from(
  { length: 24 },
  (_, index) => index + 1,
);

export type PlayerId = string;
export type ChipColor = "gold" | "gray";
export type GamePhase =
  | "DRAFT_DRAW"
  | "DRAFT_PICK"
  | "DRAFT_CLOSE"
  | "PLACE"
  | "ENDED";
export type TableSlotState = "occupied" | "taken" | "future";
export type TableSlotFace = "up" | "down";
export type DraftPickSource = "bag" | "table";

export interface ValidationResult {
  readonly valid: boolean;
  readonly error?: string;
  readonly details?: Record<string, unknown>;
}

export interface NumberChip {
  readonly chipId: string;
  readonly chipColor: ChipColor;
  readonly number: number;
}

export interface PlacedBoardToken {
  readonly ownerId: PlayerId;
  readonly chip: NumberChip;
}

export type BoardCell = PlacedBoardToken | "CENTER" | null;

export interface TableChipSlot {
  readonly slotId: string;
  readonly chip: NumberChip | null;
  readonly face: TableSlotFace | null;
  readonly trackIndex: number;
  readonly state: TableSlotState;
}

export interface KingsCrownPlayer {
  readonly playerId: PlayerId;
  readonly hand: readonly NumberChip[];
  readonly handSlots?: readonly (NumberChip | null)[];
  readonly remainingCrowns: number;
}

export interface KingsCrownState {
  readonly gameId: string;
  readonly phase: GamePhase;
  readonly playerOrder: readonly PlayerId[];
  readonly currentPlayerId: PlayerId;
  readonly firstPlayerId: PlayerId;
  readonly players: readonly KingsCrownPlayer[];
  readonly bag: readonly NumberChip[];
  readonly board: readonly (readonly BoardCell[])[];
  readonly table: readonly TableChipSlot[];
  readonly drawnSlotIds: readonly string[];
  readonly completedActionIds: readonly string[];
  readonly lastPlacementPlayerId: PlayerId | null;
  readonly winnerId: PlayerId | null;
  readonly winLine: readonly string[];
  readonly seed: number;
}

export interface InitGameOptions {
  readonly gameId?: string;
  readonly playerIds: readonly PlayerId[];
  readonly seed?: number;
  readonly firstPlayerId?: PlayerId;
}

export interface DrawDraftOptionsAction {
  readonly type: "DrawDraftOptions";
  readonly actionId: string;
  readonly playerId: PlayerId;
}

export interface PickDraftTokenAction {
  readonly type: "PickDraftToken";
  readonly actionId: string;
  readonly playerId: PlayerId;
  readonly source: DraftPickSource;
  readonly slotId?: string;
}

export interface ConfirmPlacementAction {
  readonly type: "ConfirmPlacement";
  readonly actionId: string;
  readonly playerId: PlayerId;
  readonly chipId: string;
  readonly cellId: string;
}

export interface SyncViewerStateAction {
  readonly type: "SyncViewerState";
  readonly actionId: string;
  readonly playerId: PlayerId;
}

export type KingsCrownAction =
  | DrawDraftOptionsAction
  | PickDraftTokenAction
  | ConfirmPlacementAction
  | SyncViewerStateAction;

export interface KingsCrownViewerTableSlot {
  readonly slotId: string;
  readonly chip: NumberChip | null;
  readonly face: TableSlotFace | null;
  readonly trackIndex: number;
  readonly state: TableSlotState;
}

export interface KingsCrownViewerPlayer {
  readonly playerId: PlayerId;
  readonly remainingCount: number;
  readonly isCurrent: boolean;
}

export interface KingsCrownViewerHandSlot {
  readonly slotIndex: number;
  readonly chip: NumberChip | null;
}

export interface KingsCrownViewerState {
  readonly gameId: string;
  readonly viewerPlayerId: PlayerId | "observer";
  readonly phase: GamePhase;
  readonly currentPlayerId: PlayerId;
  readonly players: readonly KingsCrownViewerPlayer[];
  readonly board: readonly (readonly BoardCell[])[];
  readonly table: readonly KingsCrownViewerTableSlot[];
  readonly bagCount: number;
  readonly ownHand: readonly NumberChip[];
  readonly ownHandSlots: readonly KingsCrownViewerHandSlot[];
  readonly opponentHandCount: number;
  readonly draftStatus: "needsDraw" | "needsPick" | "closing" | null;
  readonly legalTargetsByChipId: Readonly<Record<string, readonly string[]>>;
  readonly winnerId: PlayerId | null;
  readonly winLine: readonly string[];
}
